// =============================================================================
// POWIĄZANIE PRACOWNIKA Z OSOBĄ Z RECEPTORA — sekcja formularza i stan PIN-u
// na kafelku (kontrakt „PIN z Receptora", migracja 758, agent 0.5.0).
//
// Lista osób przychodzi z zadania `receptor.odczyt.uzytkownicy` — BEZ PIN-ów,
// z samym stanem (`zgodny`/`niezgodny`/`brak`). Agent pracuje w rytmie pulsu
// (0.8.0 co 5 s, 0.7.x co 60 s — plan 59), więc „Wczytaj" to zlecenie
// i czekanie na wynik: odpytujemy zadanie co ~5 s, zdanie o czekaniu bierzemy
// z okresu pulsu wybranego agenta (`komunikatCzekania`), a po 3 minutach mówimy
// wprost, że agent nie odpowiada — zamiast kręciołki bez końca.
//
// Stan PIN-u na kafelku czyta WYŁĄCZNIE status zadania `receptor.pin.pobierz`
// (`opisPinuZReceptora`). Wartość PIN-u nie istnieje po stronie panelu.
// =============================================================================

import { CheckCircle, Download, Link2, Loader2, RefreshCw, Search, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

import {
  ETYKIETA_STANU_PINU,
  KOMUNIKAT_BRAK_ODPOWIEDZI,
  LIMIT_CZEKANIA_NA_AGENTA_MS,
  etykietaOsoby,
  filtrujOsoby,
  komunikatBleduReceptora,
  osobyZWyniku,
  type ListaOsobReceptora,
  type OpisPinuReceptora,
  type OsobaReceptora,
  type PowiazanieReceptora,
} from '../../../pages/admin/devices/pracownicyReceptora';
import {
  odczytajOsobyReceptora,
  ostatniOdczytOsob,
  zadanieWToku,
  zadanieWgId,
  type AgentReceptora,
  type ZadanieAgenta,
} from '../../../pages/admin/devices/receptorCoreApi';
import {
  komunikatCzekania,
  zdanieORytmieAgenta,
} from '../../../pages/admin/devices/stanAgentaReceptora';

/** Rytm odpytywania zleconego odczytu osób. */
export const POLL_ODCZYTU_OSOB_MS = 5_000;

const BLAD_ODCZYTU = 'Agent nie odczytał osób z Receptora';
const NIEZNANY_KSZTALT =
  'Agent oddał listę osób w nieznanym kształcie — zaktualizuj agenta Receptora';

/** Ile osób renderujemy naraz — reszta jest pod wyszukiwarką. */
export const LIMIT_WIDOCZNYCH_OSOB = 100;

export interface WyborOsobyReceptora {
  agentId: string;
  osoba: OsobaReceptora;
}

const dataGodzina = (iso: string | null): string | null => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString('pl-PL', { dateStyle: 'short', timeStyle: 'short' });
};

/** Udany odczyt → lista do pokazania; `null`, gdy wynik nie jest listą osób. */
function odczytZZadania(
  z: ZadanieAgenta
): { lista: ListaOsobReceptora; kiedy: string | null } | null {
  const lista = osobyZWyniku(z.wynik);
  return lista ? { lista, kiedy: lista.pobrano_at ?? z.zakonczone_at ?? z.created_at } : null;
}

const TON_STANU: Record<'zgodny' | 'niezgodny' | 'brak', string> = {
  zgodny: 'text-aurora-success bg-aurora-success/10',
  niezgodny: 'text-aurora-warning bg-aurora-warning/10',
  brak: 'text-aurora-muted bg-aurora-bg-subtle',
};

interface SekcjaProps {
  organizationId: string;
  /** Agenci zdolni (aktywni, w licencji, ≥ 0.5.0) — co najmniej jeden. */
  agenci: AgentReceptora[];
  wybor: WyborOsobyReceptora | null;
  onWybor: (wybor: WyborOsobyReceptora | null) => void;
  /** Obecne powiązanie edytowanego pracownika (`null` przy dodawaniu). */
  obecne: PowiazanieReceptora | null;
  /** Nazwa INNEGO pracownika powiązanego z tą osobą przy tym agencie albo `null`. */
  zajetaPrzez: (agentId: string, idUser: number) => string | null;
  /** Formularz się zapisuje — wybór zablokowany. */
  zablokowane?: boolean;
  /** Nadpisywane WYŁĄCZNIE w testach. */
  pollMs?: number;
  limitMs?: number;
}

export function SekcjaPowiazaniaReceptora({
  organizationId,
  agenci,
  wybor,
  onWybor,
  obecne,
  zajetaPrzez,
  zablokowane = false,
  pollMs = POLL_ODCZYTU_OSOB_MS,
  limitMs = LIMIT_CZEKANIA_NA_AGENTA_MS,
}: SekcjaProps) {
  const poczatkowyAgent =
    wybor?.agentId ??
    (obecne && agenci.some((a) => a.id === obecne.agent_id) ? obecne.agent_id : agenci[0]?.id) ??
    '';
  const [agentId, setAgentId] = useState(poczatkowyAgent);
  const wybranyAgent = agenci.find((a) => a.id === agentId) ?? null;
  // Przy edycji powiązanego pracownika lista jest zwinięta — jest powiązanie,
  // a 5000 osób do przewinięcia nie jest pierwszą rzeczą, której ktoś szuka.
  const [rozwiniete, setRozwiniete] = useState(obecne === null);
  const [odczyt, setOdczyt] = useState<{ lista: ListaOsobReceptora; kiedy: string | null } | null>(
    null
  );
  // Od pierwszego renderu, żeby pusty stan nie mignął przed odczytem z bazy.
  const [ladowanie, setLadowanie] = useState(obecne === null && poczatkowyAgent !== '');
  const [zlecam, setZlecam] = useState(false);
  const [czekam, setCzekam] = useState<{ id: string; od: number } | null>(null);
  const [teraz, setTeraz] = useState(() => Date.now());
  const [blad, setBlad] = useState<string | null>(null);
  const [szukaj, setSzukaj] = useState('');
  /** Id zadania, na które czekamy TERAZ — odpowiedź po zmianie agenta jest odrzucana. */
  const czekamRef = useRef<string | null>(null);

  useEffect(() => {
    if (!rozwiniete || !agentId) return;
    let zywe = true;
    czekamRef.current = null;
    setCzekam(null);
    setOdczyt(null);
    setBlad(null);
    setLadowanie(true);
    void (async () => {
      try {
        const z = await ostatniOdczytOsob(organizationId, agentId);
        if (zywe && z) setOdczyt(odczytZZadania(z));
      } catch (err) {
        if (zywe) setBlad(komunikatBleduReceptora(err));
      } finally {
        if (zywe) setLadowanie(false);
      }
    })();
    return () => {
      zywe = false;
    };
  }, [organizationId, agentId, rozwiniete]);

  // Czekanie na zlecony odczyt — po id zadania, więc błąd agenta kończy
  // czekanie NAZWANYM powodem, a przekroczony limit — zdaniem o agencie.
  useEffect(() => {
    if (!czekam) return;
    const id = setInterval(() => {
      const chwila = Date.now();
      setTeraz(chwila);
      if (chwila - czekam.od > limitMs) {
        czekamRef.current = null;
        setCzekam(null);
        setBlad(KOMUNIKAT_BRAK_ODPOWIEDZI);
        return;
      }
      void (async () => {
        try {
          const z = await zadanieWgId(organizationId, czekam.id);
          if (czekamRef.current !== czekam.id || !z || zadanieWToku(z)) return;
          czekamRef.current = null;
          setCzekam(null);
          if (z.status === 'blad') {
            setBlad(z.blad?.trim() || BLAD_ODCZYTU);
            return;
          }
          const nowy = odczytZZadania(z);
          if (nowy) setOdczyt(nowy);
          else setBlad(NIEZNANY_KSZTALT);
        } catch {
          // Chwilowy błąd odczytu — następne odpytanie spróbuje ponownie.
        }
      })();
    }, pollMs);
    return () => clearInterval(id);
  }, [czekam, organizationId, pollMs, limitMs]);

  const wczytaj = async () => {
    if (!agentId) return;
    setBlad(null);
    setZlecam(true);
    try {
      const z = await odczytajOsobyReceptora(organizationId, agentId);
      if (zadanieWToku(z)) {
        czekamRef.current = z.id;
        setTeraz(Date.now());
        setCzekam({ id: z.id, od: Date.now() });
      } else if (z.status === 'blad') {
        setBlad(z.blad?.trim() || BLAD_ODCZYTU);
      } else {
        const nowy = odczytZZadania(z);
        if (nowy) setOdczyt(nowy);
        else setBlad(NIEZNANY_KSZTALT);
      }
    } catch (err) {
      setBlad(komunikatBleduReceptora(err));
    } finally {
      setZlecam(false);
    }
  };

  const zmienAgenta = (id: string) => {
    setAgentId(id);
    setSzukaj('');
    // IdUser ma sens tylko w bazie TEGO agenta — wybór z innej bazy wypada.
    if (wybor && wybor.agentId !== id) onWybor(null);
  };

  const widoczne = useMemo(
    () => (odczyt ? filtrujOsoby(odczyt.lista.osoby, szukaj) : []),
    [odczyt, szukaj]
  );
  const agentObecnego = obecne ? (agenci.find((a) => a.id === obecne.agent_id) ?? null) : null;
  const osobaObecna =
    obecne && odczyt && obecne.agent_id === agentId
      ? (odczyt.lista.osoby.find((o) => o.id_user === obecne.id_user) ?? null)
      : null;
  const pracuje = zlecam || czekam !== null;
  const sekundy = czekam ? Math.max(0, Math.round((teraz - czekam.od) / 1000)) : 0;

  return (
    <section
      className="pt-4 border-t border-aurora-border space-y-3"
      aria-labelledby="receptor-sekcja-tytul"
    >
      <div>
        <h3
          id="receptor-sekcja-tytul"
          className="text-sm font-semibold text-aurora-text flex items-center gap-2"
        >
          <Link2 className="w-4 h-4 text-aurora-brand" />
          Powiązanie z Receptorem
        </h3>
        <p className="text-xs text-aurora-subtle mt-1">
          Wybierz tę osobę z Receptora — pracownik dostanie jej PIN z terminala. Lista nie zawiera
          PIN-ów, tylko informację, czy da się go przenieść.
        </p>
      </div>

      {agenci.length > 1 && (
        <div>
          <label htmlFor="receptor-agent" className="field__label block mb-1.5">
            Agent Receptora
          </label>
          <select
            id="receptor-agent"
            value={agentId}
            onChange={(e) => zmienAgenta(e.target.value)}
            disabled={pracuje || zablokowane}
            className="select"
          >
            {agenci.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {obecne && (
        <div className="flex flex-wrap items-center gap-2 text-sm text-aurora-muted">
          <CheckCircle className="w-4 h-4 text-aurora-success flex-shrink-0" />
          <span>
            Obecnie powiązany z{' '}
            <strong className="text-aurora-text">
              {osobaObecna ? etykietaOsoby(osobaObecna) : `osobą nr ${obecne.id_user ?? '?'}`}
            </strong>
            {agentObecnego === null
              ? ' — przez agenta, który nie jest już aktywny; wybierz osobę ponownie'
              : agenci.length > 1
                ? ` (agent ${agentObecnego.name})`
                : null}
          </span>
          {!rozwiniete && (
            <button
              type="button"
              onClick={() => setRozwiniete(true)}
              disabled={zablokowane}
              className="btn btn--ghost btn--sm"
            >
              Zmień osobę
            </button>
          )}
        </div>
      )}

      {wybor && (
        <div className="flex items-start justify-between gap-2 rounded-ids-md border border-aurora-brand/40 bg-aurora-brand/10 p-3 text-sm">
          <div className="min-w-0">
            <p className="text-aurora-text">
              Wybrano: <strong>{etykietaOsoby(wybor.osoba)}</strong>{' '}
              <span className="text-aurora-subtle">(nr {wybor.osoba.id_user})</span>
            </p>
            {wybor.osoba.stanowisko && (
              <p className="text-xs text-aurora-subtle mt-0.5">{wybor.osoba.stanowisko}</p>
            )}
          </div>
          <button
            type="button"
            onClick={() => onWybor(null)}
            disabled={zablokowane}
            className="iconbtn flex-shrink-0"
            aria-label="Usuń wybór osoby z Receptora"
            title="Usuń wybór"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {rozwiniete && (
        <div className="space-y-2">
          {ladowanie ? (
            <p className="text-sm text-aurora-muted flex items-center gap-2" role="status">
              <Loader2 className="w-4 h-4 animate-spin" />
              Wczytuję osoby z Receptora…
            </p>
          ) : odczyt === null ? (
            <div className="rounded-ids-md border border-dashed border-aurora-border p-4 text-center">
              <p className="text-sm text-aurora-muted">
                Osoby z Receptora nie były jeszcze wczytane.
              </p>
              <p className="text-xs text-aurora-subtle mt-1">
                {zdanieORytmieAgenta(wybranyAgent, teraz)}
              </p>
              <button
                type="button"
                onClick={() => void wczytaj()}
                disabled={pracuje || zablokowane}
                className="btn btn--secondary btn--sm mt-3 inline-flex items-center gap-2"
              >
                {pracuje ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                Wczytaj osoby z Receptora
              </button>
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-xs text-aurora-subtle">
                  {odczyt.lista.liczba} osób · lista z{' '}
                  {dataGodzina(odczyt.kiedy) ?? 'nieznanej chwili'}
                </p>
                <button
                  type="button"
                  onClick={() => void wczytaj()}
                  disabled={pracuje || zablokowane}
                  className="btn btn--ghost btn--sm inline-flex items-center gap-1.5"
                  aria-label="Odśwież listę osób z Receptora"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${pracuje ? 'animate-spin' : ''}`} />
                  Odśwież
                </button>
              </div>
              {odczyt.lista.obciete && (
                <p className="text-xs text-aurora-warning">
                  Receptor oddał pierwsze {odczyt.lista.osoby.length} osób — kogoś może brakować na
                  liście.
                </p>
              )}
              <div className="relative">
                <Search className="w-4 h-4 text-aurora-subtle absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="search"
                  value={szukaj}
                  onChange={(e) => setSzukaj(e.target.value)}
                  // Enter w polu wyszukiwania NIE zapisuje pracownika.
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') e.preventDefault();
                  }}
                  placeholder="Szukaj osoby (imię, nazwisko, login, nr)…"
                  aria-label="Szukaj osoby z Receptora"
                  className="input pl-9"
                />
              </div>
              {widoczne.length === 0 ? (
                <p className="text-sm text-aurora-muted py-2">
                  {odczyt.lista.osoby.length === 0
                    ? 'Receptor nie ma aktywnych osób.'
                    : 'Nie znaleziono osoby — zmień wyszukiwanie.'}
                </p>
              ) : (
                <ul
                  aria-label="Osoby z Receptora"
                  className="max-h-60 overflow-y-auto rounded-ids-md border border-aurora-border divide-y divide-aurora-border"
                >
                  {widoczne.slice(0, LIMIT_WIDOCZNYCH_OSOB).map((o) => {
                    const zajeta = zajetaPrzez(agentId, o.id_user);
                    const wybrana = wybor?.agentId === agentId && wybor.osoba.id_user === o.id_user;
                    const obecna = obecne?.agent_id === agentId && obecne.id_user === o.id_user;
                    return (
                      <li key={o.id_user}>
                        <button
                          type="button"
                          onClick={() => onWybor(wybrana ? null : { agentId, osoba: o })}
                          disabled={zajeta !== null || zablokowane}
                          aria-pressed={wybrana}
                          className={`w-full px-3 py-2 text-left flex items-center gap-3 transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                            wybrana ? 'bg-aurora-brand/15' : 'hover:bg-aurora-bg-subtle'
                          }`}
                        >
                          <span className="flex-1 min-w-0">
                            <span className="block text-sm text-aurora-text truncate">
                              {etykietaOsoby(o)}
                              {obecna && (
                                <span className="ml-2 text-xs text-aurora-success">
                                  obecnie powiązana
                                </span>
                              )}
                            </span>
                            <span className="block text-xs text-aurora-subtle truncate">
                              {[o.stanowisko, o.login, `nr ${o.id_user}`]
                                .filter(Boolean)
                                .join(' · ')}
                              {zajeta !== null && ` · powiązana z: ${zajeta}`}
                            </span>
                          </span>
                          <span
                            className={`text-[11px] px-1.5 py-0.5 rounded flex-shrink-0 ${TON_STANU[o.stan_pinu]}`}
                          >
                            {ETYKIETA_STANU_PINU[o.stan_pinu]}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
              {widoczne.length > LIMIT_WIDOCZNYCH_OSOB && (
                <p className="text-xs text-aurora-subtle">
                  Pokazano {LIMIT_WIDOCZNYCH_OSOB} z {widoczne.length} — zawęź wyszukiwanie.
                </p>
              )}
            </>
          )}

          {czekam && (
            <p
              className="text-sm text-aurora-info flex items-center gap-2"
              role="status"
              aria-live="polite"
            >
              <Loader2 className="w-4 h-4 animate-spin flex-shrink-0" />
              {komunikatCzekania(wybranyAgent, teraz, 'osoby')}
              {sekundy > 0 && <span className="text-aurora-subtle tabular-nums">{sekundy} s</span>}
            </p>
          )}
        </div>
      )}

      {blad && (
        <p className="text-sm text-aurora-danger" role="alert">
          {blad}
        </p>
      )}
    </section>
  );
}

// ── Stan PIN-u na kafelku pracownika ────────────────────────────────────────

const TON_KAFELKA: Record<OpisPinuReceptora['ton'], string> = {
  info: 'text-sky-300',
  success: 'text-emerald-300',
  warning: 'text-amber-300',
  error: 'text-red-400',
};

interface StanPinuProps {
  /** Opis z `opisPinuZReceptora`; `null` = PIN nie był jeszcze pobierany. */
  opis: OpisPinuReceptora | null;
  idUser: number | null;
  nazwaPracownika: string;
  /** Czy pokazać przyciski (rola administracyjna). */
  mozeZarzadzac: boolean;
  /** Zlecenie pobrania właśnie idzie. */
  wToku: boolean;
  /** Błąd ostatniego zlecenia z tego kafelka. */
  blad: string | null;
  onPobierz: () => void;
  onWygeneruj: () => void;
  onSprawdz: () => void;
}

export function StanPinuReceptora({
  opis,
  idUser,
  nazwaPracownika,
  mozeZarzadzac,
  wToku,
  blad,
  onPobierz,
  onWygeneruj,
  onSprawdz,
}: StanPinuProps) {
  const czeka = opis?.stan === 'czeka';
  const odrzucony = opis?.stan === 'odrzucony' || opis?.stan === 'blad';
  const nieOdpowiada = opis?.stan === 'nie_odpowiada';
  return (
    // Kafelek jest klikalny (kartoteka) — klik w przyciski PIN-u nie nawiguje.
    <div className="space-y-1.5" onClick={(e) => e.stopPropagation()}>
      <div
        className={`flex items-start gap-2 text-xs ${opis ? TON_KAFELKA[opis.ton] : 'text-gray-400'}`}
        role="status"
        aria-live="polite"
      >
        {czeka ? (
          <Loader2 className="w-4 h-4 flex-shrink-0 animate-spin" />
        ) : (
          <Link2 className="w-4 h-4 flex-shrink-0" />
        )}
        <span>
          {opis?.tekst ??
            `Powiązany z osobą${idUser !== null ? ` nr ${idUser}` : ''} w Receptorze — PIN nie był pobierany`}
        </span>
      </div>
      {blad && (
        <p className="text-xs text-red-400" role="alert">
          {blad}
        </p>
      )}
      {mozeZarzadzac && !czeka && (
        <div className="flex flex-wrap gap-2">
          {nieOdpowiada ? (
            <button
              type="button"
              onClick={onSprawdz}
              className="btn btn--secondary btn--sm inline-flex items-center gap-1.5"
              aria-label={`Sprawdź ponownie stan PIN-u: ${nazwaPracownika}`}
            >
              <RefreshCw className="w-3.5 h-3.5" />
              Sprawdź ponownie
            </button>
          ) : (
            <button
              type="button"
              onClick={onPobierz}
              disabled={wToku}
              className="btn btn--secondary btn--sm inline-flex items-center gap-1.5"
              aria-label={`Pobierz PIN z Receptora: ${nazwaPracownika}`}
            >
              {wToku ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Download className="w-3.5 h-3.5" />
              )}
              Pobierz PIN z Receptora
            </button>
          )}
          {(odrzucony || nieOdpowiada) && (
            <button
              type="button"
              onClick={onWygeneruj}
              className="btn btn--ghost btn--sm"
              aria-label={`Wygeneruj PIN: ${nazwaPracownika}`}
            >
              Wygeneruj PIN
            </button>
          )}
        </div>
      )}
    </div>
  );
}
